import { useCallback, useEffect, useMemo, useState } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { useEditForm } from "../App/useEditForm"
import { BlockField, BlockRow } from "../Components/Block"
import { NumberInput } from "../Components/Input"
import { CloseButton } from "../Components/CloseButton"
import { EditGroup } from "../Components/EditGroup"
import { Icon, Icons } from "../Components/Icon"
import { LocalizedMessage } from "../Components/LocalizedMessage"
import { Select, SelectOption } from "../Components/Select"
import { createShipWithStore, createStoreSettingsWriters, useDataStore, useEntity, useQuery, useRetainedEntities } from "../Data"
import { IShip, IShipVesselType } from "../Types"
import { withSelectedShipSettings } from "./App/SelectedShip/Context"
import { AppRoutes, GetAppView } from "./AppView"
import { ShipDataView } from "./ShipDataView"
import { GetShipView } from "./ShipView"
import { PrintMode } from "./PrintView"
import { useShipAttachments } from "./ShipEdit/useShipAttachments"

export const ShipEditViewData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  validate(values: any): boolean {
    return values.name && values.name.length > 2 && values.vesselType > 0 && values.engineCount > 0 && values.engineCount <= 5;
  },
};

export interface ShipEditViewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  cancelEdit: any;
  initShipName?: string;
  initShipType?: IShipVesselType;
  initShipEngines?: number;
  initValidInput?: boolean;
  ship?: IShip;
}

export function ShipEditView({ cancelEdit, initShipName, initShipType, initShipEngines, initValidInput, ship }: ShipEditViewProps) {
  const app = GetApp();
  const store = useDataStore();
  const storedShip = useEntity(store, "Ship", ship?.id ?? 0);
  const settings = useQuery(store, "Settings", { shipId: ship?.id });
  const editShip = useMemo(
    () => storedShip ? withSelectedShipSettings(storedShip, settings) : ship,
    [storedShip, settings, ship],
  );
  const shipData = editShip ? Ship.shipData(editShip) : undefined;
  const t = app.getT("ShipEditView");
  const { navigateApp } = GetAppView();
  const shipView = GetShipView();
  const setPrinting = shipView?.setPrinting;
  const [type, setType] = useState<IShipVesselType | undefined>(editShip?.vesselType || initShipType);
  const [validInput, setValidInput] = useState(initValidInput ?? false);
  const shipAttachments = useShipAttachments(editShip?.id);
  const editableFields = ["name", { name: "engineCount", type: "int" }, { name: "vesselType", type: "int" }];
  const newShip = app.cloudSession() && !editShip;
  const formEditable = app.cloudSession() ? (newShip || shipView.shipCloudEditable) : app.editable;

  useRetainedEntities(["Ship", "Settings"], ship != undefined, ship?.id);

  useEffect(() => {
    if (!ship) {
      return;
    }
    if (!store.isQueryFetched("Ship", { shipId: ship.id })) {
      store.fetchQuery("Ship", { shipId: ship.id }).catch(console.error);
    }
  }, [store, ship?.id]);

  const { edited, onEdited, saveEdits, validateEdits } = useEditForm({
    save: async (values, { resetEdited }) => {
      if (!editShip && app.cloudSession()) {
        const result = await createShipWithStore(store, values);
        navigateApp(AppRoutes.ship, { shipId: result.ship.id });
      } else if (editShip) {
        await createStoreSettingsWriters(store, { shipId: editShip.id }).updateShipData(values);
        resetEdited();
      }
    },
    validate: (values) => {
      const valid = ShipEditViewData.validate(values);
      setValidInput(valid);
      return valid;
    },
  });

  const selectType = useCallback((nextType: IShipVesselType) => {
    setType(nextType);
    onEdited();
  }, [onEdited]);
  const vesselOptions: SelectOption[] = [
    { icon: Icons.MotorVessel, title: t("type_motor"), value: IShipVesselType.motor },
    { icon: Icons.SailingVessel, title: t("type_sailing"), value: IShipVesselType.sailing },
  ];
  const typeButton = vesselOptions.find((option) => option.value == type)?.title;

  return (
    <div className="ShipEditView block ">
      {editShip ? (
        <>
          <div className="ship-edit-header">
            <h2>{t("title_edit", { ship: editShip.name })}</h2>
            <div className="header-buttons">
              {setPrinting && <button className="button print-button-desktop" onClick={() => setPrinting(PrintMode.ship)}><Icon icon={Icons.Print} className="icon-large" /> {t("print")}</button>}
              <CloseButton key="close" onClick={cancelEdit} />
            </div>
          </div>
          {setPrinting && <div className="print-button-mobile"><button className="button" onClick={() => setPrinting(PrintMode.ship)}><Icon icon={Icons.Print} className="icon-large" /> {t("print")}</button></div>}
        </>
      ) : (
        <div className="new-ship-form new-ship-form-top ">
          <h2>{t("title_new")}</h2>
          <LocalizedMessage message={(message) => message.noteText.newShip} className="new-ship-message" />
        </div>
      )}

      <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} hideButton={true} manualSave={newShip} onSave={saveEdits} onValidate={validateEdits}>
        {newShip ? (
          <div className="new-ship-form new-ship-form-bottom">
            <BlockRow className="block_field_small_30"><BlockField label={t("name_min")} className="block_field_small_30"><input defaultValue={initShipName || shipData?.name} name="name" onChange={onEdited} readOnly={!formEditable} required /></BlockField></BlockRow>
            <BlockRow className="block_field_small_30">
              <BlockField label={t("type")} className="block_field_small_10"><Select button={typeButton || t("choose")} name="vesselType" onSelect={selectType} options={vesselOptions} readOnly={false} value={type} /></BlockField>
              <BlockField label={t("engines")} className="block_field_small_10"><NumberInput format="int" className="content-right" defaultValue={shipData?.engineCount || initShipEngines || ""} name="engineCount" onChange={onEdited} maxLength={1} /></BlockField>
            </BlockRow>
            <BlockRow actions={true}><BlockField><button className="button" onClick={cancelEdit}>{t("cancel")}</button><button className="button default" disabled={!validInput} type="submit">{t("create")}</button></BlockField></BlockRow>
          </div>
        ) : (
          <BlockRow className="block-row-one-row">
            <BlockField label={t("name")} className="block_field_small_30"><input defaultValue={shipData?.name} name="name" onChange={onEdited} readOnly={!formEditable} required /></BlockField>
            <BlockField label={t("type")} className="block_field_small_10"><Select button={typeButton || t("choose")} name="vesselType" onSelect={selectType} options={vesselOptions} readOnly={true} value={type} /></BlockField>
            <BlockField label={t("engines")} className="block_field_small_10"><NumberInput format="int" className="content-right" defaultValue={shipData?.engineCount || ""} name="engineCount" onChange={onEdited} readOnly={true} maxLength={1} /></BlockField>
          </BlockRow>
        )}
      </EditGroup>

      {editShip && shipData && <ShipDataView attachments={shipAttachments} data={shipData} ship={editShip} />}
    </div>
  );
}
